import {useEntitlement} from '../components/quota/EntitlementContext'
import {AUTO_MODEL_DESCRIPTION, getAutoModel} from './auto-model'
import {type CopilotChatModel, type CopilotModel, CopilotPlan} from './copilot-chat-types'
import {copilotFeatureFlags} from './copilot-feature-flags'
import {FIRST_PARTY_VENDORS} from './model-vendors'

const OPEN_AI_LOGO = '/images/modules/marketplace/models/families/openai-logo.svg'
const ANTHROPIC_LOGO = '/images/modules/marketplace/models/families/anthropic.svg'
const GEMINI_LOGO = '/images/modules/marketplace/models/families/gemini.svg'
const XAI_LOGO = '/images/modules/marketplace/models/families/xai.svg'
const XAI_LOGO_DARK = '/images/modules/marketplace/models/families/xai-dark.svg'
const MICROSOFT_LOGO = '/images/modules/marketplace/models/families/microsoft.svg'

const LOGO_BY_VENDOR = new Map([
  ['Azure OpenAI', OPEN_AI_LOGO],
  ['OpenAI', OPEN_AI_LOGO],
  ['Anthropic', ANTHROPIC_LOGO],
  ['Google', GEMINI_LOGO],
  ['xAI', XAI_LOGO],
  ['Microsoft', MICROSOFT_LOGO],
])

type DescriptionRule = [(name: string, family: string) => boolean, string]

// Ordered lookup table: first matching rule wins.
// Each entry is [predicate, description].
const MODEL_DESCRIPTION_RULES: DescriptionRule[] = [
  // Auto: the backend curation (copilot-chat synthesizedAutoModel) vends an `auto`
  // entry with no description — CAPI's model schema has no description field, so
  // descriptions are derived here for every model. Map Auto to its canonical
  // explainer so the backend-vended row carries the same copy as the frontend
  // AUTO_MODEL singleton instead of arriving description-less.
  [(n, f) => f === 'auto' || n === 'auto', AUTO_MODEL_DESCRIPTION],
  // Claude sub-variants (must come before the generic Claude fallback)
  [
    (n, f) => (f.includes('claude') || n.includes('claude')) && n.includes('opus'),
    'Excellent for complex analysis, coding tasks, and nuanced creative writing.',
  ],
  [
    (n, f) => (f.includes('claude') || n.includes('claude')) && n.includes('sonnet'),
    'Balanced Claude model offering strong performance for everyday coding and chat tasks at faster speeds.',
  ],
  [
    (n, f) => (f.includes('claude') || n.includes('claude')) && n.includes('haiku'),
    'Fastest and most compact Claude model. Ideal for quick responses and simple tasks.',
  ],
  [(n, f) => f.includes('claude') || n.includes('claude'), 'Versatile Claude model for coding and general assistance.'],
  // Codex (check before generic GPT to avoid falling into the GPT branch)
  [
    (n, f) => f.includes('codex') || n.includes('codex'),
    'OpenAI Codex model specialized for code generation, debugging, and software development tasks.',
  ],
  // GPT sub-variants
  [
    (n, f) => (f.includes('gpt') || n.includes('gpt')) && n.includes('mini'),
    'Lightweight GPT model for quick responses and simple tasks with low latency.',
  ],
  [
    (n, f) => (f.includes('gpt') || n.includes('gpt')) && n.includes('copilot'),
    'GPT model fine-tuned for Copilot code completions.',
  ],
  [
    (n, f) => (f.includes('gpt') || n.includes('gpt')) && n.includes('4o'),
    'Optimized GPT-4 model with faster responses and multimodal capabilities.',
  ],
  [
    (n, f) => (f.includes('gpt') || n.includes('gpt')) && n.includes('4.1'),
    'Enhanced GPT-4 model with improved instruction following and coding performance.',
  ],
  [(n, f) => f.includes('gpt') || n.includes('gpt'), 'OpenAI GPT model for coding and general assistance.'],
  // Gemini sub-variants
  [
    (n, f) => (f.includes('gemini') || n.includes('gemini')) && n.includes('flash'),
    'Fast and efficient Gemini model optimized for quick responses and high throughput.',
  ],
  [
    (n, f) => (f.includes('gemini') || n.includes('gemini')) && n.includes('pro'),
    "Google's advanced Gemini Pro model with strong reasoning and coding capabilities.",
  ],
  [
    (n, f) => f.includes('gemini') || n.includes('gemini'),
    'Google Gemini model with balanced performance for coding and general assistance.',
  ],
  // Grok
  [
    (n, f) => f.includes('grok') || n.includes('grok'),
    'xAI Grok model optimized for fast code generation and development tasks.',
  ],
]

function getModelCapabilitiesDescription(model: CopilotModel): string | undefined {
  const name = model.name.toLowerCase()
  const family = model.capabilities.family.toLowerCase()
  return MODEL_DESCRIPTION_RULES.find(([test]) => test(name, family))?.[1]
}

export function findDefaultModel(
  copilotModels: CopilotChatModel[] | null,
  plan: CopilotPlan | null,
  premiumInteractionsQuotaExceeded: boolean,
  overagesEnabled: boolean,
): CopilotChatModel {
  // Use the fallback model if the user is out of quota.
  const defaultModel =
    copilotFeatureFlags.premiumRequestQuotasEnabled &&
    plan !== CopilotPlan.IndividualFree &&
    premiumInteractionsQuotaExceeded &&
    !overagesEnabled
      ? copilotModels?.find(m => m.is_chat_fallback === true)
      : copilotModels?.find(m => m.is_chat_default === true)
  if (!defaultModel) {
    return generateDefaultModelForPlan(plan)
  }
  return defaultModel
}

export const BASE_MODEL_ID = 'copilot-base'

export function generateLegacyDefaultModel(): CopilotChatModel {
  return {
    capabilities: {
      family: 'gpt-4o',
      limits: {
        // eslint-disable-next-line camelcase
        max_prompt_tokens: 20000,
        vision: {
          // eslint-disable-next-line camelcase
          supported_media_types: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
        },
      },
      supports: {
        // eslint-disable-next-line camelcase
        parallel_tool_calls: true,
        // eslint-disable-next-line camelcase
        tool_calls: true,
      },
      tokenizer: 'o200k_base',
      type: 'chat',
    },
    id: 'gpt-4o',
    name: 'GPT 4o',
    version: 'gpt-4o-2024-05-13',
    displayName: 'GPT 4o',
    vendor: 'Azure OpenAI',
    logoURL: OPEN_AI_LOGO,
    hasLimitedCapabilities: false,
    preview: false,
    isThirdParty: false,
    // eslint-disable-next-line camelcase
    model_picker_enabled: true,
  }
}

export function generateDefaultModel(): CopilotChatModel {
  return {
    capabilities: {
      family: 'claude-haiku-4.5',
      limits: {
        // eslint-disable-next-line camelcase
        max_prompt_tokens: 200000,
        vision: {
          // eslint-disable-next-line camelcase
          supported_media_types: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
        },
      },
      supports: {
        // eslint-disable-next-line camelcase
        parallel_tool_calls: true,
        // eslint-disable-next-line camelcase
        tool_calls: true,
      },
      tokenizer: 'claude',
      type: 'chat',
    },
    id: 'claude-haiku-4.5',
    name: 'Claude Haiku 4.5',
    displayName: 'Claude Haiku 4.5',
    // eslint-disable-next-line camelcase
    model_picker_enabled: true,
    preview: false,
    vendor: 'Anthropic',
    logoURL: ANTHROPIC_LOGO,
    isThirdParty: true,
    hasLimitedCapabilities: false,
    version: '2025-10-01',
  }
}

export function useDefaultModel(copilotModels: CopilotChatModel[] | null): CopilotChatModel {
  const {plan, premiumInteractionsQuotaExceeded, overagesEnabled} = useEntitlement()
  return getDefaultModel(copilotModels, plan, premiumInteractionsQuotaExceeded, overagesEnabled)
}

// Plan-aware hardcoded fallback used when CAPI's `/models` response is empty
// or missing an `is_chat_default` entry. Auto launch flags can make Auto the
// default for known plans; otherwise the plan falls back to Claude Haiku 4.5.
export function generateDefaultModelForPlan(plan: CopilotPlan | null): CopilotChatModel {
  const isFreeOrEdu = plan === CopilotPlan.IndividualFree || plan === CopilotPlan.IndividualStudent
  if (copilotFeatureFlags.autoModePickerFreeEdu && isFreeOrEdu) {
    return getAutoModel()
  }
  // All SKUs: Auto is the chat default/fallback when the backend can't be
  // reached (no CAPI is_chat_default). Gated on a known plan so an unknown
  // (null) plan keeps the Haiku default.
  if (copilotFeatureFlags.autoModePickerPaid && plan != null) {
    return getAutoModel()
  }
  return generateDefaultModel()
}

// If the forceLegacyChatDefaultModel feature flag is enabled, use the legacy default model.
// Otherwise, use the CAPI default model, or fallback if quota is exhausted.
// findDefaultModel returns any backend-marked `is_chat_default` entry directly.
export function getDefaultModel(
  copilotModels: CopilotChatModel[] | null,
  plan: CopilotPlan | null,
  premiumInteractionsQuotaExceeded?: boolean,
  overagesEnabled?: boolean,
): CopilotChatModel {
  if (copilotFeatureFlags.forceLegacyChatDefaultModel) {
    return generateLegacyDefaultModel()
  }
  return findDefaultModel(copilotModels, plan, !!premiumInteractionsQuotaExceeded, !!overagesEnabled)
}

export function toCopilotChatModels(copilotModels: CopilotModel[]) {
  const baseModels = copilotModels ?? [generateDefaultModel()]

  const allowedModels = baseModels.map(toAllowedCopilotChatModel).filter(m => !!m)
  return allowedModels.filter(m => m.model_picker_enabled || m.is_upsell)
}

function toAllowedCopilotChatModel(copilotModel: CopilotModel): CopilotChatModel | null {
  if (!isChatModel(copilotModel)) return null
  if (!isAllowed(copilotModel)) return null
  let logoUrl = LOGO_BY_VENDOR.get(copilotModel.vendor)
  if (copilotModel.vendor === 'xAI' && document.querySelector('[data-color-mode="dark"]') !== null) {
    logoUrl = XAI_LOGO_DARK
  }
  const description = copilotModel.description ?? getModelCapabilitiesDescription(copilotModel)

  return {
    ...copilotModel,
    description,
    displayName: displayName(copilotModel),
    hasLimitedCapabilities: hasLimitedCapabilities(copilotModel, copilotFeatureFlags.copilotChatO1Tools),
    isThirdParty: isThirdParty(copilotModel),
    logoURL: logoUrl,
  }
}

export function hasLimitedCapabilities(m: CopilotModel, o1ToolsEnabled: boolean) {
  if (m.capabilities.family === 'o1-ga') {
    return !o1ToolsEnabled
  }
  if (m.capabilities.family === 'o3') {
    return true
  }
  return !m.capabilities.supports.tool_calls
}

function isAllowed(m: CopilotModel) {
  if (!m.model_picker_enabled && !m.is_upsell) return false

  if (m.policy && m.policy.state !== 'enabled' && !m.policy.terms) {
    // If the model has a policy that must be accepted but we have no terms to show the user,
    // there is no valid way for them to use the model and we should not show it.
    return false
  }

  return true
}

function isThirdParty(m: CopilotModel) {
  return !FIRST_PARTY_VENDORS.has(m.vendor)
}

function isChatModel(m: CopilotModel): m is CopilotChatModel {
  return m.capabilities.type === 'chat'
}

export function isCustomModel(
  model: CopilotModel,
): model is CopilotModel & {custom_model: NonNullable<CopilotModel['custom_model']>} {
  return model.custom_model != null
}

const betaLabels = ['(Beta)', '(Preview)']
function displayName(m: CopilotModel) {
  for (const suffix of betaLabels) {
    if (m.name.endsWith(suffix)) {
      return m.name.replace(suffix, '').trim()
    }
  }
  return m.name
}
